import { useStateHooks } from "../../../state"
import { TableName } from "../../../tables"
import { defined, toList } from "../../../utils/listUtils"
import { round } from "../../../utils/typeUtils"
import { PaymentCategory, PaymentType, useCashFundLoaders, usePaymentTypeHooks, usePaymentTypeLoaders } from "../../administration"
import { DeliveryStatus, useDeliveryLoaders } from "../../deliveries"
import { FiscalDocumentStatus } from "../../fiscal-documents"
import { useRoadmapLoaders } from "../../logistics"
import { operationHelpers, PaymentStatus, usePriceOutlineHooks } from "../../operations"
import { Customer, CustomersDebtResult, HoardOrder, HoardType, SaleOperation, SalePaymentItem, SalePaymentItemData, useHoardHooks } from "../../sales"

export const useSaleOperationHooks = () => {
    const { describeNoun } = operationHelpers
    const { isCreditAccount } = usePaymentTypeHooks()
    const { findFiltered } = useStateHooks()
    const { getAdjustedTotal } = usePriceOutlineHooks()
    const { getPendingCredit } = useHoardHooks()

    const paymentTypeLoaders = usePaymentTypeLoaders()
    const deliveryLoaders = useDeliveryLoaders()
    const roadmapLoaders = useRoadmapLoaders()
    const cashFundLoaders = useCashFundLoaders()

    const describePaymentItemName = (paymentItem: SalePaymentItem | SalePaymentItemData) => {
        const { creditInFavor, isRounding, paymentTypeId } = paymentItem
        if (isRounding) return 'Redondeo'
        if (creditInFavor) return 'Crédito a Favor'
        const paymentType = paymentTypeLoaders().findPaymentType(paymentTypeId)
        // Un cobro puede quedar sin medio de pago (por ejemplo, cubierto 100% con crédito a favor).
        return paymentType?.name || 'Sin medio de pago'
    }

    const describePaymentItemDetail = (paymentItem: SalePaymentItemData) => {
        const { creditInFavor, isRounding, paymentTypeId, cashFundId, checkName } = paymentItem
        if (isRounding) return '-'
        const paymentType = paymentTypeLoaders().findPaymentType(paymentTypeId)
        const isCheck = paymentType?.category === PaymentCategory.CHECK
        const cashFund = cashFundLoaders().findCashFund(cashFundId)
        return creditInFavor ? '-' : isCheck ? checkName! : cashFund?.name || '-'
    }
    
    const getPaymentTypes = (operation: SaleOperation): PaymentType[] => {
        const { paymentTypeId, paymentItems } = operation
        const paymentTypeIds = paymentItems.length > 0 ? defined(paymentItems.map(paymentItem => paymentItem.paymentTypeId)) : toList(paymentTypeId)
        const { findPaymentType } = paymentTypeLoaders()
        return defined(paymentTypeIds.map(paymentTypeId => findPaymentType(paymentTypeId)))
    }

    const isEditableSync = (operation: SaleOperation): boolean => {
        const { paymentStatus, deliveryStatus, invoiceStatus, cancelled, archived } = operation
        const deliveries = findFiltered(TableName.DELIVERIES2, { operationId: operation.id })
        const deliveryIds = deliveries.map(delivery => delivery.id)
        const deliveryRoadmaps = findFiltered(TableName.DELIVERY_ROADMAPS, { deliveryId: deliveryIds })
        const hasRoadmap = deliveryRoadmaps.length > 0
        return paymentStatus === PaymentStatus.PENDING && deliveryStatus === DeliveryStatus.PENDING && invoiceStatus === FiscalDocumentStatus.PENDING && !hasRoadmap && !cancelled && !archived
    }

    const isEditable = async (operation: SaleOperation): Promise<boolean> => {
        const { paymentStatus, deliveryStatus, invoiceStatus, cancelled, archived } = operation
        const { loadOperationDeliveries } = deliveryLoaders()
        const deliveries = await loadOperationDeliveries(operation.id)
        const { loadDeliveriesRoadmapsLinks } = roadmapLoaders()
        const deliveryRoadmaps = await loadDeliveriesRoadmapsLinks(deliveries.map(delivery => delivery.id))
        const hasRoadmap = deliveryRoadmaps.length > 0
        return paymentStatus === PaymentStatus.PENDING && deliveryStatus === DeliveryStatus.PENDING && invoiceStatus === FiscalDocumentStatus.PENDING && !hasRoadmap && !cancelled && !archived
    }

    const hasDeliveries = async (operation: SaleOperation): Promise<boolean> => {
        const { loadOperationDeliveries } = deliveryLoaders()
        const deliveries = await loadOperationDeliveries(operation.id)
        return deliveries.length > 0
    }

    const isDeliveryOrderEmitable = (operation: SaleOperation): boolean => {
        const { paymentStatus, items, cancelled, archived } = operation

        const table = saleOperationHelpers.getTable(operation)

        const isDelivered = operation.deliveryStatus === DeliveryStatus.DELIVERED
        let hasPendingCredit = false
        if (table === TableName.HOARD_ORDERS) {
            const hoard = operation as HoardOrder
            hasPendingCredit = hoard.type === HoardType.FINANCIAL && getPendingCredit(hoard) > 0
        }
        const pendingToBeEmitted = items.reduce((acc, item) => acc + (item.deliveryStats?.pending || 0), 0)

        const isPayed = paymentStatus === PaymentStatus.PAYED
        const isPaymentCreditAccount = isCreditAccount(operation.paymentTypeId)
        const hasLatePayment = !!operation.latePayment
        const isBlockedByPayment = !isPayed && !isPaymentCreditAccount && !hasLatePayment
        
        return !isDelivered && (hasPendingCredit || pendingToBeEmitted > 0) && !isBlockedByPayment && !cancelled && !archived
    }

    const isPayable = (operation: SaleOperation): boolean => {
        const { findPaymentType } = paymentTypeLoaders()
        const isCreditPayment = findPaymentType(operation.paymentTypeId)?.category === PaymentCategory.CREDIT_ACCOUNT
        const isPayed = operation.paymentStatus === PaymentStatus.PAYED
        return !isCreditPayment && !isPayed
    }

    // A manual credit note corrects the price of an operation, paid or not (unpaid, it just lowers what the customer owes).
    // It is only offered once the operation can't be edited anymore: until then the price is corrected by editing it.
    // On a financial hoard the hoarded value doesn't change: the customer paid less for the same value.
    const isManualCreditNoteAllowed = (
        table: TableName,
        operation: SaleOperation,
        isEditable: boolean
    ): boolean => {
        const { cancelled, archived } = operation
        return !isEditable && getAdjustedTotal(table, operation) > 0 && !cancelled && !archived
    }

    // Deuda por cliente: cuenta corriente (neta del crédito a favor) + saldo impago de ventas/acopios con otras formas de pago
    const getCustomersDebt = (
        operations: SaleOperation[],
        customers: Customer[]
    ): CustomersDebtResult => {
        const creditAccountDebt: Record<string, number> = {}
        const otherDebt: Record<string, number> = {}
        const skippedOperations: SaleOperation[] = []

        operations
            .filter(operation => operation.paymentStatus !== PaymentStatus.PAYED && !operation.cancelled && !operation.archived)
            .forEach(operation => {
                const table = saleOperationHelpers.getTable(operation)
                let total: number
                try {
                    total = getAdjustedTotal(table, operation)
                } catch (e) {
                    // Pasa si algún producto de la operación no tiene precios cargados (ej. producto eliminado)
                    console.warn(`Deuda de clientes: no se pudo calcular el total de ${describeNoun(table)} #${operation.code}`, e)
                    skippedOperations.push(operation)
                    return
                }
                const debt = Math.max(0, total - (Number(operation.paidAmount) || 0))
                const debtByCustomer = isCreditAccount(operation.paymentTypeId) ? creditAccountDebt : otherDebt
                debtByCustomer[operation.customerId] = (debtByCustomer[operation.customerId] || 0) + debt
            })

        const customerIds = new Set([...Object.keys(creditAccountDebt), ...Object.keys(otherDebt)])

        const customersDebt = Array.from(customerIds)
            .map(customerId => {
                const customer = customers.find(c => c.id === customerId)
                // El crédito a favor descuenta solo la deuda de cuenta corriente
                const netCreditAccountDebt = round(Math.max(0, (creditAccountDebt[customerId] || 0) - (customer?.creditInFavor || 0)))
                const customerOtherDebt = round(otherDebt[customerId] || 0)
                return {
                    customerId,
                    customer,
                    creditAccountDebt: netCreditAccountDebt,
                    otherDebt: customerOtherDebt,
                    totalDebt: round(netCreditAccountDebt + customerOtherDebt)
                }
            })
            .filter(customerDebt => customerDebt.totalDebt > 0)
            .sort((a, b) => b.totalDebt - a.totalDebt)

        return { customersDebt, skippedOperations }
    }

    const paymentReceiptFilePath = (
        table: TableName,
        operation: SaleOperation,
        index = 1,
        ext = 'pdf'
    ) => {
        const tableLabel = table === TableName.SALE_ORDERS ? 'sales' : 'hoards'
        return `${tableLabel}/${operation.id}/paymentItems/${index}/receipt/${saleOperationHelpers.paymentReceiptFileName()}-${index}_${describeNoun(table)}-${operation.code}.${ext}`
    }

    return {
        describePaymentItemName,
        describePaymentItemDetail,
        getPaymentTypes,
        isEditableSync,
        isEditable,
        hasDeliveries,
        isDeliveryOrderEmitable,
        isPayable,
        isManualCreditNoteAllowed,
        getCustomersDebt,
        paymentReceiptFilePath
    }
}

export const saleOperationHelpers = {
    getTable: (operation: SaleOperation): TableName =>
        Object.prototype.hasOwnProperty.call(operation, 'pricesList') ? TableName.HOARD_ORDERS : TableName.SALE_ORDERS,

    paymentReceiptFileName: (): string => 'Comprobante-Pago',

    isArchivable: (operation: SaleOperation): boolean => {
        const { paymentStatus, deliveryStatus, invoiceStatus, archived } = operation
        const isHoard = saleOperationHelpers.getTable(operation) === TableName.HOARD_ORDERS
        // Un acopio puede quedar con saldo sin facturar: no se archiva hasta cerrar su facturación.
        const isInvoiceClosed = !isHoard || invoiceStatus === FiscalDocumentStatus.AUTHORIZED
        return paymentStatus === PaymentStatus.PAYED && deliveryStatus === DeliveryStatus.DELIVERED && isInvoiceClosed && !archived
    },

    sortPaymentItems: (operation: SaleOperation) => {
        return operation.paymentItems.sort((a, b) =>
            a.creditInFavor ? -1
            : b.creditInFavor ? 1
            : (a.paymentTypeId || '').localeCompare(b.paymentTypeId || '')
        )
    }
}
